import React, { useState } from 'react';
import { 
    Box, Paper, Typography, Grid, Table, TableBody, TableCell, 
    TableHead, TableRow, Button, 
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    RadioGroup, FormControlLabel, Radio, FormControl, FormLabel,
    IconButton
} from '@mui/material';
import { FaUserPlus, FaTrash } from 'react-icons/fa';
import dayjs from 'dayjs';

const TournamentSheet = ({ tournament, onRegister, onRemove, currentUserId, isAdmin, familyMembers }) => {
    
    // Default category to first available if exists
    const defaultCategory = tournament?.categories?.[0]?.name || 'Allgemein';
    const foodOptions = tournament?.foodOptions || []; // Array of strings e.g. ["Kuchen", "Salat"]

    // Dialog state
    const [openDialog, setOpenDialog] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [registerType, setRegisterType] = useState('self'); // 'self' or 'guest'
    const [guestName, setGuestName] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(defaultCategory);
    const [food, setFood] = useState('');
    const [bookingNotes, setBookingNotes] = useState('');

    if (!tournament) return null;

    const handleOpenRegister = (initialCategory) => {
        setSelectedCategory(initialCategory || tournament.categories?.[0]?.name || 'Allgemein');
        setRegisterType('self');
        setGuestName('');
        setFood('');
        setBookingNotes('');
        setOpenDialog(true);
    };

    const handleRegister = async () => {
        setIsSubmitting(true);
        const data = {
            name: registerType === 'self' ? '' : guestName, 
            isGuest: registerType !== 'self',
            category: selectedCategory,
            food,
            bookingNotes
        };
        // Expect onRegister to return a Promise that resolves when done,
        // or just wait a bit. We'll set it false when the parent closes the dialog 
        // or we close it here.
        try {
            await onRegister(tournament._id, data);
            setOpenDialog(false);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getCategoryColor = (name) => {
        if (!name) return '#f5f5f5';
        const lower = name.toLowerCase();
        if (lower.includes('damen') || lower.includes('women') || lower.includes('frau')) return '#fce4ec'; 
        if (lower.includes('herren') || lower.includes('men') || lower.includes('män')) return '#e3f2fd'; 
        if (lower.includes('mixed') || lower.includes('mix')) return '#e0f2f1'; 
        if (lower.includes('jugend') || lower.includes('kind')) return '#f3e5f5'; 
        return '#f5f5f5'; 
    };

    // Check if registration is allowed
    const isPastEvent = dayjs().isAfter(dayjs(tournament.date));
    // Allow registration throughout the entire deadline day - block only from start of next day
    const nextDayStart = tournament.deadline ? dayjs(tournament.deadline).add(1, 'day').startOf('day') : null;
    const isPastDeadline = tournament.deadline && !dayjs().isBefore(nextDayStart);

    const renderList = (categoryName) => {
        const participants = tournament.participants.filter(p => p.category === categoryName);
        const active = participants.filter(p => p.status === 'Active');
        const reserve = participants.filter(p => p.status === 'Reserve'); 

        const maxPlayers = tournament.categories.find(c => c.name === categoryName)?.maxPlayers || 12;

        const renderRows = (list, startIndex, maxCount, isReserve = false) => {
            const rows = [];
            const limit = isReserve ? Math.max(list.length, 0) : maxCount;

            for (let i = 0; i < limit; i++) {
                const p = list[i];
                rows.push(
                    <TableRow key={i} sx={{ height: 33 }}>
                        <TableCell padding="none" align="center" sx={{ width: 40, borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                            <Typography variant="body2" color="text.secondary">
                                {startIndex + i + 1}
                            </Typography>
                        </TableCell>
                        <TableCell sx={{ borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                            {p ? (
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            fontWeight: p.isGuest ? 'normal' : 'bold',
                                            fontStyle: p.isGuest ? 'italic' : 'normal'
                                        }}
                                    >
                                        {p.name} {p.isGuest && "(Gast)"}
                                        {p.bookingNotes && <span style={{fontSize: '0.7em', color: 'gray', marginLeft: 4}}>({p.bookingNotes})</span>}
                                    </Typography>
                                    {(isAdmin || p.registeredBy === currentUserId) && (
                                        <IconButton size="small" color="error" onClick={() => onRemove(tournament._id, p._id)}>
                                            <FaTrash size={12}/>
                                        </IconButton>
                                    )}
                                </Box>
                            ) : null}
                        </TableCell>
                        {/* Dynamic Food Columns */}
                        {foodOptions.map((opt, idx) => (
                             <TableCell key={idx} padding="none" align="center" sx={{ width: 50, borderLeft: idx === 0 ? '1px solid rgba(224, 224, 224, 1)' : '' }}>
                                {p?.food === opt && <Typography fontWeight="bold">X</Typography>}
                            </TableCell>
                        ))}
                    </TableRow>
                );
            }
            return rows;
        };

        return (
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <Box bgcolor={getCategoryColor(categoryName)} p={1} borderBottom={1} borderColor="divider">
                    <Typography variant="h6" align="center" fontWeight="bold">
                        {categoryName}
                    </Typography>
                </Box>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Nr</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                            {/* Dynamic Food Headers */}
                            {foodOptions.map((opt, idx) => (
                                <TableCell key={idx} align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                                    {opt}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderRows(active, 0, maxPlayers)}
                    </TableBody>
                </Table>
                
                {(reserve.length > 0) && (
                    <>
                        <Box bgcolor="#fafafa" p={1} borderTop={1} borderBottom={1} borderColor="divider">
                             <Typography variant="subtitle2" align="center" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                Reserve
                            </Typography>
                        </Box>
                        <Table size="small">
                             <TableBody>
                                {renderRows(reserve, 0, reserve.length, true)}
                            </TableBody>
                        </Table>
                    </>
                )}
            </Paper>
        );
    };

    const categoryCount = tournament.categories.length;
    let gridXs = 12;
    let gridSm = 12;
    let gridMd = 4;

    if (categoryCount === 1) {
        gridSm = 12;
        gridMd = 12; 
    } else if (categoryCount === 2) {
        gridSm = 6;
        gridMd = 6;
    } else {
        gridSm = 6;
        gridMd = 4;
    }

    return (
        <Box sx={{ p: 2 }}>
            {/* Register Button */}
            <Box mb={3} display="flex" justifyContent="flex-end">
                <Button 
                    variant="contained" 
                    startIcon={<FaUserPlus />} 
                    onClick={() => handleOpenRegister(tournament.categories?.[0]?.name)} 
                    disabled={isPastEvent || isPastDeadline}
                >
                    Anmelden
                </Button>
            </Box>

            {/* Description / Banner */}
             {tournament.description && (
                <Box mb={3} p={2} bgcolor="#fffde7" borderRadius={2} border={1} borderColor="#fff9c4">
                    <Typography variant="body1">
                        {tournament.description}
                    </Typography>
                </Box>
            )}

            {/* Contact Info */}
            <Box mb={2}>
                <Typography variant="body2" color="text.secondary">
                    <b>Ansprechpartner:</b> {tournament.contact || 'Vorstand'}
                </Typography>
            </Box>

            {/* Lists Grid */}
            <Grid container spacing={3}>
                {tournament.categories.map(cat => (
                    <Grid item xs={gridXs} sm={gridSm} md={gridMd} key={cat.name}>
                        {renderList(cat.name)}
                    </Grid>
                ))}
            </Grid>

            {/* Registration Dialog */}
            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>Anmeldung für {tournament.name}</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" gap={2} mt={1}>
                        <FormControl>
                            <FormLabel id="reg-type-label">Wen möchtest du anmelden?</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby="reg-type-label"
                                value={registerType}
                                onChange={(e) => setRegisterType(e.target.value)}
                            >
                                <FormControlLabel value="self" control={<Radio />} label="Mich selbst" />
                                {familyMembers && familyMembers.length > 0 && (
                                    <FormControlLabel value="family" control={<Radio />} label="Familie" />
                                )}
                                <FormControlLabel value="guest" control={<Radio />} label="Einen Gast" />
                            </RadioGroup>
                        </FormControl>

                        {registerType === 'guest' && (
                            <TextField
                                label="Name des Gastes"
                                fullWidth
                                value={guestName}
                                onChange={(e) => setGuestName(e.target.value)}
                            />
                        )}

                        {registerType === 'family' && (
                            <TextField
                                select
                                label="Familienmitglied auswählen"
                                fullWidth
                                value={guestName} 
                                onChange={(e) => setGuestName(e.target.value)}
                                SelectProps={{ native: true }}
                            >
                                <option value="" disabled>Bitte wählen</option>
                                {familyMembers.map((m) => (
                                    <option key={m._id} value={`${m.firstName} ${m.lastName}`}>
                                        {m.firstName} {m.lastName}
                                    </option>
                                ))}
                            </TextField>
                        )}

                        <FormControl>
                            <FormLabel>Kategorie</FormLabel>
                            <RadioGroup
                                row
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                {tournament.categories.map(cat => (
                                    <FormControlLabel 
                                        key={cat.name} 
                                        value={cat.name} 
                                        control={<Radio />} 
                                        label={cat.name} 
                                    />
                                ))}
                            </RadioGroup>
                        </FormControl>

                        {/* Configurable Food Options */}
                        {foodOptions.length > 0 && (
                            <FormControl>
                                <FormLabel>Optionen (Mitbringsel)</FormLabel>
                                <RadioGroup
                                    row
                                    value={food}
                                    onChange={(e) => setFood(e.target.value)}
                                >
                                    <FormControlLabel value="" control={<Radio />} label="Nichts" />
                                    {foodOptions.map((opt, idx) => (
                                        <FormControlLabel key={idx} value={opt} control={<Radio />} label={opt} />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        )}
                        
                        {(registerType === 'guest' || registerType === 'family') && (
                             <TextField
                                label="Notizen (z.B. Bezahlt)"
                                fullWidth
                                value={bookingNotes}
                                onChange={(e) => setBookingNotes(e.target.value)}
                                size="small"
                            />
                        )}

                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Abbrechen</Button>
                    <Button 
                        onClick={handleRegister} 
                        variant="contained" 
                        disabled={((registerType === 'guest' || registerType === 'family') && !guestName) || isSubmitting}
                    >
                        {isSubmitting ? 'Wird angemeldet...' : 'Anmelden'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default TournamentSheet;
